import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FaUpload, FaCheckCircle, FaFileAlt } from 'react-icons/fa';

export default function ApplyVerifiedOperator() {
  const { token, currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Form State
  const [idDocUrl, setIdDocUrl] = useState('');
  const [boatingLicenseUrl, setBoatingLicenseUrl] = useState('');
  const [safetyAgreementSigned, setSafetyAgreementSigned] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/profile/${currentUser.uid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setProfile(data.profile);
        }
      } catch (err) {
        console.error("Error fetching profile", err);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [currentUser, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!safetyAgreementSigned) {
      setError('You must sign the safety agreement.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/operator-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          idDocUrl: idDocUrl || 'https://example.com/mock-id.pdf', // Mock for now unless file upload added
          boatingLicenseUrl: boatingLicenseUrl || 'https://example.com/mock-license.pdf',
          knowledgeQuizScore: 100, // Hardcoded for v1 flow
          safetyAgreementSigned
        })
      });

      const data = await response.json();
      if (data.success) {
        // Redirect to dashboard
        navigate('/dashboard/community');
      } else {
        setError(data.error || 'Failed to submit application');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred during submission.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  const status = profile?.verifiedOperatorStatus?.status;

  if (status === 'approved') {
    return (
      <div className="py-4 community-dashboard-container">
        <Alert variant="success" className="text-center">
          <FaCheckCircle size={40} className="mb-3 d-block mx-auto" />
          <h4 className="alert-heading">You are already a Verified Operator!</h4>
          <p className="mb-0">You have full access to the Verified Operator Forum and your public badge is active.</p>
        </Alert>
        <Button variant="warning" onClick={() => navigate('/dashboard/community/operator-forum')}>
          Go to Private Forum
        </Button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="py-4 community-dashboard-container">
        <Alert variant="warning" className="text-center">
          <Spinner animation="grow" size="sm" className="me-2" />
          <h4 className="alert-heading d-inline-block m-0 align-middle">Application Under Review</h4>
          <p className="mt-3 mb-0">Your application is currently being reviewed by our moderation team. This usually takes 1-2 business days.</p>
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard/community')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="py-4 community-dashboard-container">
      <h2 className="mb-4">Become a Verified Bass Boat Operator</h2>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={handleSubmit}>
                <h5 className="mb-3">1. Identify Verification</h5>
                <p className="text-muted small mb-4">
                  Upload a valid government-issued ID (Driver's License or Passport).
                </p>
                <Form.Group className="mb-4">
                  <div className="border border-dashed p-4 text-center rounded bg-light">
                    <FaUpload size={24} className="text-secondary mb-2" />
                    <p className="mb-1 fw-bold">Click to upload ID Document</p>
                    <p className="text-muted small mb-0">PDF, JPG, or PNG (Max 5MB)</p>
                    <Form.Control type="file" className="d-none" />
                  </div>
                </Form.Group>

                <h5 className="mb-3">2. Boating Safety Certification</h5>
                <p className="text-muted small mb-4">
                  Upload your state-approved boating safety license or certificate (if applicable in your state).
                </p>
                <Form.Group className="mb-5">
                  <div className="border border-dashed p-4 text-center rounded bg-light">
                    <FaUpload size={24} className="text-secondary mb-2" />
                    <p className="mb-1 fw-bold">Click to upload Boating License</p>
                    <p className="text-muted small mb-0">PDF, JPG, or PNG (Max 5MB)</p>
                    <Form.Control type="file" className="d-none" />
                  </div>
                </Form.Group>

                <h5 className="mb-3">3. BBR Safety Agreement</h5>
                <div className="bg-light p-3 border rounded mb-4" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <h6>Operator Code of Conduct</h6>
                  <p className="small text-muted mb-2">As a verified operator on BBR, I agree to:</p>
                  <ul className="small text-muted mb-0">
                    <li className="mb-1">Maintain my watercraft in excellent, safe working condition.</li>
                    <li className="mb-1">Ensure all required safety equipment (life jackets, fire extinguishers, flares) is present and functional.</li>
                    <li className="mb-1">Provide clear, accurate instructions to renters regarding the specific operation of the bass boat and electronics.</li>
                    <li className="mb-1">Uphold the BBR community standards of respect, honesty, and professionalism.</li>
                    <li className="mb-1">Communicate promptly and resolve issues amicably.</li>
                  </ul>
                </div>
                
                <Form.Group className="mb-5">
                  <Form.Check 
                    type="checkbox"
                    id="safety-agreement"
                    label={<span className="fw-bold">I have read and agree to the BBR Safety Agreement and Code of Conduct.</span>}
                    checked={safetyAgreementSigned}
                    onChange={(e) => setSafetyAgreementSigned(e.target.checked)}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-3 pt-3 border-top">
                  <Button variant="outline-secondary" onClick={() => navigate('/dashboard/community')}>
                    Cancel
                  </Button>
                  <Button variant="warning" type="submit" disabled={submitting || !safetyAgreementSigned}>
                    {submitting ? <Spinner size="sm" className="me-2" /> : <FaFileAlt className="me-2" />}
                    Submit Application
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm border-0 bg-light">
            <Card.Body>
              <h5 className="mb-3">Why Get Verified?</h5>
              <ul className="list-unstyled mb-0">
                <li className="mb-3 d-flex gap-2">
                  <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                  <span><strong>Build Trust:</strong> Renters are 3x more likely to book with a Verified Operator.</span>
                </li>
                <li className="mb-3 d-flex gap-2">
                  <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                  <span><strong>Premium Badge:</strong> Stand out in the community and search results.</span>
                </li>
                <li className="d-flex gap-2">
                  <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                  <span><strong>Private Forum:</strong> Gain access to exclusive discussions with top BBR owners.</span>
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
